// @flow

import moment from 'moment'
import queryString from 'query-string'
import { isInclusivelyAfterDay } from 'react-dates'
import {
  getPlacePredictions,
  getPlaceDetail,
} from 'frontend_core/dist/helpers/places'
import { numberWithComma } from 'frontend_core/dist/helpers/format'
import { isMobile } from 'common/screen'
import {
  ReservedOptions,
  StayRoomTypes,
} from 'frontend_core/dist/constants/search'
import type { SearchConditionType } from 'frontend_core/dist/helpers/types/search-condition'

export function queryStringParse(str: string = ''): Object {
  return queryString.parse(str, { arrayFormat: 'bracket' })
}

export function queryStringStringify(obj: Object = {}): string {
  return queryString.stringify(obj, { arrayFormat: 'bracket' })
}

function toCamelCase(str: string): string {
  return str.replace(/_./g, matched => {
    return matched.charAt(1).toUpperCase()
  })
}

export function convertToSearchSessionIdParams(locationSearch: string): Object {
  const snakeCaseQueries = queryStringParse(locationSearch)
  let queries = {}
  for (let key in snakeCaseQueries) {
    queries[toCamelCase(key)] = snakeCaseQueries[key]
  }
  return { searchSessionId: queries['searchSessionId'] }
}

export function convertToSearchConditionType(
  locationSearch: string,
  perPage: number = 20,
): SearchConditionType {
  const snakeCaseQueries = queryStringParse(locationSearch)
  let queries = {}
  for (let key in snakeCaseQueries) {
    queries[toCamelCase(key)] = snakeCaseQueries[key]
  }

  return convertDefaultSearchCondition(queries, perPage)
}

export function convertDefaultSearchCondition(
  queries,
  perPage: number = 20,
): SearchConditionType {
  let amenities = []
  if (Array.isArray(queries.amenities)) {
    amenities = queries.amenities
  } else if (queries.amenities) {
    amenities = [queries.amenities]
  }

  let sponsoredPromotionId = []
  if (Array.isArray(queries.sponsoredPromotionId)) {
    sponsoredPromotionId = queries.sponsoredPromotionId
  } else if (queries.sponsoredPromotionId) {
    sponsoredPromotionId = [queries.sponsoredPromotionId]
  }

  let locationText = queries.locationText || ''
  let state = queries.state
  if (queries.state && !queries.geocode) {
    locationText = i18n.t(`state.${queries.state}`)
  } else {
    state = ''
  }

  return {
    page: parseInt(queries.page, 10) || 1,
    perPage: queries.perPage || perPage,
    startedAt: queries.startedAt
      ? moment(queries.startedAt).format('YYYY-MM-DD')
      : '',
    endedAt: queries.endedAt
      ? moment(queries.endedAt).format('YYYY-MM-DD')
      : '',
    geocode: queries.geocode || '',
    minCapacity: parseInt(queries.minCapacity, 10) || 0,
    locationText,
    orderBy: queries.orderBy || '',
    hasDirectReservationPlans: queries.hasDirectReservationPlans === 'true',
    hasLastMinuteDiscountPlans: queries.hasLastMinuteDiscountPlans === 'true',
    ownerRank:
      queries.ownerRank === ReservedOptions.TopHost
        ? ReservedOptions.TopHost
        : '',
    minPrice: parseInt(queries.minPrice, 10) || null,
    maxPrice: parseInt(queries.maxPrice, 10) || null,
    amenities,
    keyword: queries.keyword || '',
    eventType: queries.eventType,
    state,
    sponsoredPromotionId,
    withRecommend: queries.withRecommend === 'true',
  }
}

export function convertSnakeCaseSearchCondition(queries): SearchConditionType {
  return {
    page: queries.page,
    per_page: queries.per_page,
    started_at: queries.startedAt,
    endedAt: queries.ended_at,
    geocode: queries.geocode,
    min_capacity: queries.minCapacity,
    location_text: queries.locationText,
    order_by: queries.orderBy || '',
    has_direct_reservation_plans: queries.hasDirectReservationPlans,
    has_last_minute_discount_plans: queries.hasLastMinuteDiscountPlans,
    owner_rank: queries.ownerRank,
    min_price: queries.minPrice,
    max_price: queries.maxPrice,
    amenities: queries.amenities,
    keyword: queries.keyword || '',
    event_type: queries.eventType,
    state: queries.state,
    sponsored_promotion_id: queries.sponsoredPromotionId,
    with_recommend: queries.withRecommend,
  }
}

export function getOrderByObject(orderBy: string) {
  switch (orderBy) {
    case 'PRICE':
      return {
        field: orderBy,
        direction: 'ASC',
      }
    case 'CREATED_AT':
      return {
        field: orderBy,
        direction: 'DESC',
      }
    default:
      return null
  }
}

export function convertToVariables(
  searchCondition: SearchConditionType,
): Object {
  return {
    page: searchCondition.page,
    startedAt:
      searchCondition.startedAt !== '' ? searchCondition.startedAt : null,
    endedAt: searchCondition.endedAt !== '' ? searchCondition.endedAt : null,
    geocode:
      searchCondition.geocode !== ''
        ? decodeURIComponent(searchCondition.geocode)
        : null,
    minCapacity: searchCondition.minCapacity || 0,
    orderBy: getOrderByObject(searchCondition.orderBy),
    hasDirectReservationPlans: searchCondition.hasDirectReservationPlans
      ? true
      : null,
    hasLastMinuteDiscountPlans: searchCondition.hasLastMinuteDiscountPlans
      ? true
      : null,
    ownerRank:
      searchCondition.ownerRank === ReservedOptions.TopHost
        ? ReservedOptions.TopHost
        : null,
    minPrice: searchCondition.minPrice,
    maxPrice: searchCondition.maxPrice,
    amenities:
      searchCondition.amenities.length > 0
        ? searchCondition.amenities.join(',')
        : null,
    keyword:
      searchCondition.keyword !== ''
        ? decodeURIComponent(searchCondition.keyword)
        : null,
    eventType: searchCondition.eventType,
    state:
      searchCondition.state !== ''
        ? decodeURIComponent(searchCondition.state)
        : null,
    searchSessionId:
      searchCondition.searchSessionId !== ''
        ? searchCondition.searchSessionId
        : null,
    sponsoredPromotionId:
      searchCondition.sponsoredPromotionId.length > 0
        ? searchCondition.sponsoredPromotionId.join(',')
        : null,
    withRecommend: searchCondition.withRecommend ? true : null,
  }
}

// 地図に表示するスペース位置の差分kmの計算
export function calcMapStatus(roomList: Array<Object>) {
  let west = 180.0
  let east = -180.0
  let north = -90.0
  let south = 90.0
  roomList.forEach(room => {
    const roomLatitude = parseFloat(room.latitude)
    const roomLongitude = parseFloat(room.longitude)
    if (roomLatitude > north) north = roomLatitude
    if (roomLatitude < south) south = roomLatitude
    if (roomLongitude > east) east = roomLongitude
    if (roomLongitude < west) west = roomLongitude
  })
  const diffLat = north - south
  const diffLng = east - west
  const diffLatKm = diffLat / 0.0090133729745762
  const diffLngKm = diffLng / 0.010966404715491394
  const diff = diffLatKm > diffLngKm ? diffLatKm : diffLngKm

  // 差分kmからzoomLevelを計算
  let zoomLevel = 12
  if (diff > 500) {
    zoomLevel = 6
  } else if (diff > 200) {
    zoomLevel = 7
  } else if (diff > 100) {
    zoomLevel = 8
  } else if (diff > 50) {
    zoomLevel = 9
  } else if (diff > 20) {
    zoomLevel = 10
  } else if (diff > 10) {
    zoomLevel = 11
  } else if (diff > 5) {
    zoomLevel = 12
  } else if (diff > 2) {
    zoomLevel = 13
  } else if (diff > 1) {
    zoomLevel = 14
  } else {
    zoomLevel = 15
  }

  // 中心位置
  const centerLat = (north + south) / 2.0
  const centerLng = (east + west) / 2.0
  const centerLatLng = {
    lat: centerLat,
    lng: centerLng,
  }

  return {
    zoomLevel,
    centerLatLng,
  }
}

// 日付ピッカー用
const focusedInputStartDate = 'startDate'

export default {
  mapPlacePredictionsToViewModel: (autoCompleteItems: Array<Object>) =>
    (autoCompleteItems || []).map(item => ({
      name: item.terms[0].value,
      detail: item.description,
      original: item,
    })),

  focusedInputStartDate,

  getReservedOptions: (
    uiSearchCondition: SearchConditionType,
    i18n: Object,
  ) => [
    {
      id: ReservedOptions.hasDirectReservationPlans,
      label: i18n.t('page.search.reserveOption.direct'),
      beforeIcon: 'flash',
      afterIcon: 'question-circle',
      hoverMessage: i18n.t('page.search.reserveOption.directHelp'),
      checked: uiSearchCondition.hasDirectReservationPlans,
    },
    {
      id: ReservedOptions.hasLastMinuteDiscountPlans,
      label: i18n.t('page.search.reserveOption.lastMinute'),
      beforeIcon: 'discount',
      afterIcon: 'question-circle',
      hoverMessage: i18n.t('page.search.reserveOption.lastMinuteHelp'),
      checked: uiSearchCondition.hasLastMinuteDiscountPlans,
    },
    {
      id: ReservedOptions.ownerRank,
      label: i18n.t('page.search.reserveOption.topHost'),
      beforeIcon: 'host',
      afterIcon: 'question-circle',
      hoverMessage: i18n.t('page.search.reserveOption.topHostHelp'),
      checked: uiSearchCondition.ownerRank,
    },
  ],

  defaultDayPickerProps: (
    startedAt: ?moment,
    endedAt: ?moment,
    focusedInput: ?string,
    setUIState: Function,
    uiContainerId: string,
    i18n: Object,
  ) => ({
    monthFormat: i18n.t('page.search.dayPickerMonthFormat'),
    numberOfMonths: isMobile() ? 1 : 2,
    hideKeyboardShortcutsPanel: true,
    isOutsideRange: (day: any) => !isInclusivelyAfterDay(day, moment()),
    startDate: startedAt,
    endDate: endedAt,
    onDatesChange: (changed: { startDate: moment, endDate: moment }) => {
      setUIState(uiContainerId, {
        startedAt: changed.startDate ? changed.startDate : null,
        endedAt: changed.endDate ? changed.endDate : null,
      })
    },
    focusedInput,
    onFocusChange: (changedFocusedInput: string) => {
      setUIState(uiContainerId, {
        focusedInput: !changedFocusedInput
          ? focusedInputStartDate
          : changedFocusedInput,
      })
    },
  }),

  defaultSingleDayPickerProps: (
    date: ?moment,
    focusedInput: ?string,
    setUIState: Function,
    uiContainerId: string,
    i18n: Object,
  ) => ({
    monthFormat: i18n.t('page.search.dayPickerMonthFormat'),
    numberOfMonths: isMobile() ? 1 : 2,
    hideKeyboardShortcutsPanel: true,
    isOutsideRange: (day: any) => !isInclusivelyAfterDay(day, moment()),
    date: date,
    onDateChange: (date: moment) => {
      setUIState(uiContainerId, {
        startedAt: date ? date : null,
      })
    },
    onFocusChange: (changedFocusedInput: string) => {
      setUIState(uiContainerId, {
        focusedInput: !changedFocusedInput
          ? focusedInputStartDate
          : changedFocusedInput,
      })
    },
  }),

  getCheckInValue: (startedAt: ?string, endedAt: ?string, i18n: Object) => {
    if (startedAt && endedAt) {
      return i18n.t('page.search.checkIn', {
        startDate: moment(startedAt).format(i18n.t('page.shared.dateFormat2')),
        endDate: moment(endedAt).format(i18n.t('page.shared.dateFormat2')),
      })
    } else if (startedAt) {
      return i18n.t('page.search.startedAt', {
        startDate: moment(startedAt).format(i18n.t('page.shared.dateFormat2')),
      })
    }
    return ''
  },

  areaInput: async (
    text: string,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
    autocompleteService: any,
    i18n: Object,
  ) => {
    // エリアを空にした場合
    if (!text) {
      setUIState(uiContainerId, {
        isOpenedAutocomplete: false,
        selectedPlaceId: undefined,
        searchCondition: Object.assign({}, uiSearchCondition, {
          geocode: '',
          locationText: '',
          state: '',
          page: 1,
        }),
      })
    }

    // エリアに入力があった場合
    setUIState(uiContainerId, {
      locationText: text,
    })
    try {
      const predictions = await getPlacePredictions(autocompleteService, text)
      setUIState(uiContainerId, {
        autoCompleteItems: predictions.concat({
          terms: [{ value: i18n.t('page.shared.unspecified') }],
          description: '',
          unspecified: true,
        }),
        isOpenedAutocomplete: true,
      })
    } catch (error) {
      // TODO: 通信エラー時の処理
      setUIState(uiContainerId, {
        autoCompleteItems: [],
        isOpenedAutocomplete: false,
      })
    }
  },
  areaSelected: async (
    item: any,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
    areaInput: any,
  ) => {
    if (item.unspecified) {
      setUIState(uiContainerId, {
        isOpenedAutocomplete: false,
        selectedPlaceId: undefined,
        locationText: '',
        searchCondition: Object.assign({}, uiSearchCondition, {
          geocode: '',
          locationText: '',
          state: '',
          page: 1,
        }),
      })
    }

    try {
      const geocode = await getPlaceDetail(item.place_id, areaInput)
      setUIState(uiContainerId, {
        isOpenedAutocomplete: false,
        selectedPlaceId: item.place_id,
        locationText: undefined,
        searchCondition: Object.assign({}, uiSearchCondition, {
          geocode,
          locationText: item.terms[0].value,
          state: '',
          page: 1,
        }),
      })
    } catch (error) {
      // TODO: 通信エラー時の処理
      console.error(error)
    }
  },
  areaClose: (setUIState: Function, uiContainerId: string) => {
    setUIState(uiContainerId, {
      isOpenedAutocomplete: false,
      locationText: '',
      autoCompleteItems: [],
    })
  },

  checkInClicked: (
    showCheckInCalendar: boolean,
    setUIState: Function,
    uiContainerId: string,
  ) => {
    if (showCheckInCalendar) {
      setUIState(uiContainerId, { showCheckInCalendar: false })
      return
    }

    setUIState(uiContainerId, {
      showCheckInCalendar: true,
      focusedInput: focusedInputStartDate,
    })
  },
  checkInReset: (
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    setUIState(uiContainerId, {
      startedAt: null,
      endedAt: null,
      showCheckInCalendar: false,
      searchCondition: Object.assign({}, uiSearchCondition, {
        startedAt: '',
        endedAt: '',
        page: 1,
      }),
    })
  },
  checkInApply: (
    startedAt: ?moment,
    endedAt: ?moment,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    if (startedAt && endedAt) {
      setUIState(uiContainerId, {
        searchCondition: Object.assign({}, uiSearchCondition, {
          startedAt: startedAt.format('YYYY-MM-DD'),
          endedAt: endedAt.format('YYYY-MM-DD'),
          page: 1,
        }),
        showCheckInCalendar: false,
      })
    }
  },
  checkInHourlyApply: (
    startedAt: ?moment,
    endedAt: ?moment,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    if (startedAt) {
      setUIState(uiContainerId, {
        searchCondition: Object.assign({}, uiSearchCondition, {
          startedAt: startedAt.format('YYYY-MM-DD'),
          endedAt: null,
          page: 1,
        }),
        showCheckInCalendar: false,
      })
    }
  },
  checkInClose: (setUIState: Function, uiContainerId: string) => {
    setUIState(uiContainerId, {
      startedAt: null,
      endedAt: null,
      showCheckInCalendar: false,
    })
  },
  capacityClicked: (
    showCapacity: boolean,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    if (showCapacity) {
      setUIState(uiContainerId, { showCapacity: false })
      return
    }
    setUIState(uiContainerId, {
      showCapacity: true,
      minCapacity: uiSearchCondition.minCapacity,
    })
  },
  capacityAdd: (
    addNumber: number,
    setUIState: Function,
    uiContainerId: string,
    previousMinCapacity: ?number,
  ) => {
    const minCapacity = (previousMinCapacity || 0) + addNumber
    if (!Number.isNaN(minCapacity) && minCapacity >= 0) {
      setUIState(uiContainerId, {
        minCapacity,
      })
    }
  },
  capacityInput: (
    text: string,
    setUIState: Function,
    uiContainerId: string,
  ) => {
    if (text === '') {
      setUIState(uiContainerId, {
        minCapacity: undefined,
      })
    }

    const minCapacity = parseInt(text, 10)
    if (!Number.isNaN(minCapacity)) {
      setUIState(uiContainerId, {
        minCapacity,
      })
    }
  },
  capacityReset: (
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    setUIState(uiContainerId, {
      searchCondition: Object.assign({}, uiSearchCondition, {
        minCapacity: undefined,
        page: 1,
      }),
      showCapacity: false,
    })
  },
  capacityApply: (
    setUIState: Function,
    uiContainerId: string,
    minCapacity: ?number,
    uiSearchCondition: SearchConditionType,
  ) => {
    if (!minCapacity || minCapacity < 0) {
      return
    }
    setUIState(uiContainerId, {
      showCapacity: false,
      searchCondition: Object.assign({}, uiSearchCondition, { minCapacity }),
    })
  },
  capacityClose: (setUIState: Function, uiContainerId: string) => {
    setUIState(uiContainerId, {
      showCapacity: false,
    })
  },
  eventSelected: (
    item: any,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    setUIState(uiContainerId, {
      searchCondition: Object.assign({}, uiSearchCondition, {
        eventType: item,
      }),
    })
  },
  priceTypeSelected: (
    item: any,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    setUIState(uiContainerId, {
      searchCondition: Object.assign({}, uiSearchCondition, {
        priceType: item,
      }),
    })
  },
  sartedHourSelected: (
    item: any,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    setUIState(uiContainerId, {
      searchCondition: Object.assign({}, uiSearchCondition, {
        sartedHour: item,
      }),
    })
  },
  endedHourSelected: (
    item: any,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    setUIState(uiContainerId, {
      searchCondition: Object.assign({}, uiSearchCondition, {
        endedHour: item,
      }),
    })
  },
  minCapacitySelected: (
    item: any,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    setUIState(uiContainerId, {
      searchCondition: Object.assign({}, uiSearchCondition, {
        minCapacity: item,
      }),
    })
  },
  maxCapacitySelected: (
    item: any,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    setUIState(uiContainerId, {
      searchCondition: Object.assign({}, uiSearchCondition, {
        maxCapacity: item,
      }),
    })
  },
  reserveOptionValueChanged: (
    item: ?any,
    value: boolean,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    if (item) {
      const newReserveOption = { page: 1 }
      if (item.id === ReservedOptions.ownerRank) {
        newReserveOption[`${item.id}`] = value ? ReservedOptions.TopHost : ''
      } else {
        newReserveOption[`${item.id}`] = value
      }
      setUIState(uiContainerId, {
        searchCondition: Object.assign({}, uiSearchCondition, newReserveOption),
      })
    }
  },
  stayRoomTypeValueChanged: (
    item: any,
    value: boolean,
    setUIState: Function,
    uiContainerId: string,
    uiSearchCondition: SearchConditionType,
  ) => {
    if (item) {
      let newStayRoomType = (uiSearchCondition.stayRoomType || []).concat()
      if (value) {
        newStayRoomType.push(item.id)
      } else {
        newStayRoomType = newStayRoomType.filter(type => item.id !== type)
      }
      setUIState(uiContainerId, {
        searchCondition: Object.assign({}, uiSearchCondition, {
          stayRoomType: newStayRoomType,
          page: 1,
        }),
      })
    }
  },
}

export function getUserAvailablePoints(
  viewer?: ?{ pointBalance: { availableAmount: number } },
): ?string {
  const { pointBalance } = viewer || {}

  if (!pointBalance || pointBalance.availableAmount === 0) {
    return null
  }

  return numberWithComma(pointBalance.availableAmount)
}



// WEBPACK FOOTER //
// ./src/helpers/search.js